import { Button, message, Modal, Select, Table } from "antd";
import React, { useEffect, useState } from "react";
import styles from "../../pages/main/Admin/index.module.scss";
import { API, graphqlOperation } from "aws-amplify";
import { retrieveIntervalFromModelName, TRADING_PAIRS } from "../../utils";
import { availableTimeIntervals } from "../../pages/main/ModelPerformance";
import * as mutations from "../../graphql/mutations";
import { without } from "lodash";
import Service, { ElAdminEventLogService } from "../../services";

const { Option } = Select;

const columns = [
  {
    title: "modelName1",
    dataIndex: "modelName1",
    key: "modelName1",
    width: "25%",
  },
  {
    title: "modelName2",
    dataIndex: "modelName2",
    key: "modelName2",
    width: "25%",
    textWrap: "word-break",
  },
  {
    title: "disabled",
    dataIndex: "disabled",
    key: "disabled",
    width: "5%",
    render: (text, record, i) => text.toString(),
  },
  {
    title: "symbol",
    dataIndex: "symbol",
    key: "symbol",
    width: "10%",
  },
  {
    title: "temporarilyDisabledSymbols",
    dataIndex: "temporarilyDisabledSymbols",
    key: "temporarilyDisabledSymbols",
    render: (text, record, i) => text.join(","),
  },
  {
    title: "tradingSymbols",
    dataIndex: "tradingSymbols",
    key: "tradingSymbols",
    render: (text, record, i) => text.join(","),
  },
];

const ModelsTab = () => {
  const [modelsOfAllSymbols, setModelsOfAllSymbols] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState("BTC/USD");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isUpdatingModels, setUpdatingModels] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modelsOfSelectedSymbol, setModelsOfSelectedSymbol] = useState([]);

  useEffect(() => {
    setModelsOfSelectedSymbol(modelsOfAllSymbols.filter((model) => model.tradingSymbols.includes(selectedSymbol)));
  }, [modelsOfAllSymbols, selectedSymbol]);

  useEffect(() => {
    // fetch all models when:
    // 1. user visit the page for the first time
    // 2. after models are updated
    if (!isUpdatingModels) {
      listTradeModels();
    }
  }, [isUpdatingModels]);

  useEffect(() => {
    const resetSelectedModels = () => {
      const enabledModelRowKeys = modelsOfSelectedSymbol
        .filter((model) => !model.temporarilyDisabledSymbols.includes(selectedSymbol))
        .map((model) => getRowKeyFromModelNames(model.modelName1, model.modelName2));
      setSelectedRowKeys(enabledModelRowKeys);
    };
    resetSelectedModels();
  }, [modelsOfAllSymbols, modelsOfSelectedSymbol, selectedSymbol]);

  const listTradeModels = async () => {
    const allModels = await Service.listTradeModels();
    const allEnabledModels = allModels.filter(
      (model) => model.tradingSymbols && model.tradingSymbols.length > 0 // filter enabled models
    );
    setModelsOfAllSymbols(allEnabledModels);
  };

  const getRowKeyFromModelNames = (modelName1, modelName2) => modelName1 + "," + modelName2;

  // update temporarilyDisabledSymbols of the model
  const updateModel = async (modelName1, modelName2, temporarilyDisabledSymbols) => {
    return API.graphql(
      graphqlOperation(mutations.updateTradeModels, {
        input: {
          modelName1,
          modelName2,
          temporarilyDisabledSymbols,
        },
      })
    );
  };

  const getModelsToUpdate = () => {
    const modelsToEnable = [];
    const modelsToDisable = [];
    modelsOfSelectedSymbol.forEach((model) => {
      const isSelected = selectedRowKeys.includes(getRowKeyFromModelNames(model.modelName1, model.modelName2));
      const isEnabled = !model.temporarilyDisabledSymbols.includes(selectedSymbol);
      if (isSelected && !isEnabled) {
        modelsToEnable.push(model);
      }
      if (!isSelected && isEnabled) {
        modelsToDisable.push(model);
      }
    });
    return { modelsToEnable, modelsToDisable };
  };

  const getModalContent = () => {
    const columns = [
      {
        title: "modelName1",
        dataIndex: "modelName1",
        key: "modelName1",
      },
      {
        title: "modelName2",
        dataIndex: "modelName1",
        key: "modelName2",
      },
    ];
    const { modelsToEnable, modelsToDisable } = getModelsToUpdate();

    return (
      <div>
        {modelsToEnable.length > 0 && (
          <Table
            expandable={{
              defaultExpandAllRows: true,
            }}
            title={() => (
              <>
                Models to <b>ENABLE</b>:
              </>
            )}
            columns={columns}
            dataSource={getDataSourceFromModels(modelsToEnable)}
            rowSelection={{ getCheckboxProps: () => ({ disabled: true }) }}
            rowKey={(record) => getRowKeyFromModelNames(record.modelName1, record.modelName2)}
            scroll={{ y: 600 }}
            pagination={false}
          />
        )}
        {modelsToDisable.length > 0 && (
          <Table
            expandable={{
              defaultExpandAllRows: true,
            }}
            title={() => (
              <>
                Models to <b>DISABLE</b>:
              </>
            )}
            columns={columns}
            dataSource={getDataSourceFromModels(modelsToDisable)}
            rowSelection={{ getCheckboxProps: () => ({ disabled: true }) }}
            rowKey={(record) => getRowKeyFromModelNames(record.modelName1, record.modelName2)}
            scroll={{ y: 600 }}
            pagination={false}
          />
        )}
      </div>
    );
  };

  const handleButtonOnClick = () => {
    const { modelsToEnable, modelsToDisable } = getModelsToUpdate();
    if (modelsToEnable.length === 0 && modelsToDisable.length === 0) {
      message.warning("Nothing to update...");
    } else {
      setModalOpen(true);
    }
  };

  const handleModalOnOk = async () => {
    setUpdatingModels(true);
    try {
      const { modelsToEnable, modelsToDisable } = getModelsToUpdate();

      const updateAndLogPromises = [
        ...modelsToEnable.map(async (model) => {
          const temporarilyDisabledSymbols = without(model.temporarilyDisabledSymbols, selectedSymbol);
          await updateModel(model.modelName1, model.modelName2, temporarilyDisabledSymbols);
          await ElAdminEventLogService.saveEnableDisableModelEvent(model.modelName1, model.modelName2, selectedSymbol, "enable");
        }),
        ...modelsToDisable.map(async (model) => {
          const temporarilyDisabledSymbols = [...model.temporarilyDisabledSymbols, selectedSymbol];
          await updateModel(model.modelName1, model.modelName2, temporarilyDisabledSymbols);
          await ElAdminEventLogService.saveEnableDisableModelEvent(model.modelName1, model.modelName2, selectedSymbol, "disable");
        }),
      ];

      await Promise.all(updateAndLogPromises);
      message.success("Updated successfully");
    } catch (error) {
      message.error(error.message ? error.message : String(error));
    } finally {
      setUpdatingModels(false);
      setModalOpen(false);
    }
  };

  const handleModalOnCancel = () => {
    setModalOpen(false);
  };

  // get available symbols based on models
  const getSymbolsFromModels = (models) => {
    const symbolSet = new Set();
    models.forEach((model) => {
      model.tradingSymbols.forEach((tradingSymbol) => symbolSet.add(tradingSymbol));
    });
    const symbols = TRADING_PAIRS.map((symbol) => (symbol.startsWith("1000") ? symbol.slice(4) : symbol)).filter((symbol) =>
      symbolSet.has(symbol)
    );
    return symbols;
  };

  const getDataSourceFromModels = (models) => {
    // get available intervals based on models
    const intervalSet = new Set();
    models.forEach((model) => intervalSet.add(retrieveIntervalFromModelName(model.modelName1)));
    // sort intervals based on pre-defined orders
    const intervals = availableTimeIntervals.filter((interval) => intervalSet.has(interval));
    // generate datasource grouped by intervals
    const dataSource = intervals.map((interval) => {
      const modelsWithSameInterval = models.filter((model) => retrieveIntervalFromModelName(model.modelName1) === interval);
      return {
        modelName1: interval,
        modelName2: interval,
        disabled: "",
        symbol: "",
        temporarilyDisabledSymbols: [],
        tradingSymbols: [],
        children: modelsWithSameInterval,
      };
    });
    return dataSource;
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
    },
    checkStrictly: false,
  };

  return (
    <div className={styles.admin}>
      <div className={styles.adminHeader}>
        <div className={styles.title}>Models</div>
        <div className={styles.adminPanel}>
          Symbol:
          <Select
            showSearch
            value={selectedSymbol}
            style={{ width: 120, marginRight: 10, marginLeft: 10 }}
            onChange={(value) => {
              setSelectedSymbol(value);
            }}
            optionFilterProp="children"
          >
            {getSymbolsFromModels(modelsOfSelectedSymbol).map((symbol) => (
              <Option key={symbol} value={symbol}>
                {symbol}
              </Option>
            ))}
          </Select>
          <Button type="primary" style={{ marginLeft: 8 }} onClick={handleButtonOnClick}>
            Enable/Disable Models
          </Button>
          <Modal
            width={800}
            title={`Update models for ALL ${selectedSymbol} trading sessions:`}
            open={isModalOpen}
            onOk={handleModalOnOk}
            onCancel={handleModalOnCancel}
            confirmLoading={isUpdatingModels}
          >
            {getModalContent()}
          </Modal>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={getDataSourceFromModels(modelsOfSelectedSymbol)}
        rowSelection={rowSelection}
        rowKey={(record) => getRowKeyFromModelNames(record.modelName1, record.modelName2)}
        scroll={{ x: 1000, y: 600 }}
        pagination={false}
      />
    </div>
  );
};

export default ModelsTab;
